import type { EventoPublico, PeticionPresupuesto, TipoPublico } from '../api/ops/compraEntradas';
import type { MiPerfil } from '../api/ops/clienteFinal';
import { diaLargo, etiquetaDia, horaEn, hoyEn } from '../pantallas/reservas/fechas';
import { telefonoLegible } from './logica';

/**
 * Reglas de la compra pública de entradas (10.13, G901), sin pantalla: qué se puede elegir, las líneas y los nombres de
 * las nominativas (G3) y los textos de cada estado del evento. El importe nunca se calcula aquí: lo da la API
 * (`quoteTicketOrder`); qué está a la venta lo dice `PublicEvent` (`on_sale_now`, `available`, `sale_starts_at`).
 */
export type Carrito = Readonly<Record<string, number>>;
export type Nombres = Readonly<Record<string, readonly string[]>>;

/** Lo que dice el evento antes de elegir: a la venta o por qué no. */
export type EstadoVenta = 'a-la-venta' | 'agotado' | 'solo-listas' | 'venta-no-abierta' | 'sin-venta';

export function estadoVenta(e: Pick<EventoPublico, 'sale_mode' | 'sold_out' | 'sale_starts_at' | 'ticket_types'>): EstadoVenta {
    if (e.sale_mode === 'lists_only') {
        return 'solo-listas';
    }
    if (e.sold_out === true) {
        return 'agotado';
    }
    if (e.sale_starts_at != null) {
        return 'venta-no-abierta';
    }
    return e.ticket_types.some(sePuedeComprar) ? 'a-la-venta' : 'sin-venta';
}

/** Un tipo se elige si está a la venta ahora y quedan (o el negocio no dice cuántas quedan). */
export function sePuedeComprar(t: TipoPublico): boolean {
    return t.on_sale_now && t.available !== 0;
}

/** El tope de un tipo: el suyo, lo que queda y lo que deja el pedido entero. */
export function maximoDe(t: TipoPublico, carrito: Carrito, maxPorPedido: number): number {
    const otros = Object.entries(carrito).filter(([id]) => id !== t.id).reduce((n, [, x]) => n + x, 0);
    const libres = Math.max(0, maxPorPedido - otros);
    return Math.min(t.max_per_order ?? libres, t.available ?? libres, libres);
}

export function enCarrito(c: Carrito): number {
    return Object.values(c).reduce((n, x) => n + x, 0);
}

/** Las líneas de `quoteTicketOrder` y `createOnlineTicketOrder`, con los nombres en las nominativas (G3). */
export function lineasDeCompra(c: Carrito, nominativo: boolean, nombres: Nombres): PeticionPresupuesto['lines'] {
    return Object.entries(c)
        .filter(([, n]) => n > 0)
        .map(([id, n]) => ({ ticket_type_id: id, quantity: n, ...(nominativo ? { holder_names: (nombres[id] ?? []).slice(0, n).map((x) => x.trim()) } : {}) }));
}

/** En un evento nominativo, cada entrada lleva un nombre de al menos 2 letras. */
export function faltanNombres(c: Carrito, nominativo: boolean, nombres: Nombres): boolean {
    return nominativo && Object.entries(c).some(([id, n]) => n > 0 && Array.from({ length: n }, (_, i) => (nombres[id]?.[i] ?? '').trim()).some((x) => x.length < 2));
}

/** «Sáb 17 oct · 21:30» (o «Hoy · 21:30») en la zona del evento. */
export function cuandoEvento(e: Pick<EventoPublico, 'local_start_date' | 'local_start_time'>, hoy: string): string {
    const dia = etiquetaDia(e.local_start_date, hoy);
    return `${dia} · ${e.local_start_time.slice(0, 5)}`;
}

/** «Sábado 17 de octubre · 21:30». */
export function cuandoLargo(e: Pick<EventoPublico, 'local_start_date' | 'local_start_time'>): string {
    const d = diaLargo(e.local_start_date);
    return `${d.slice(0, 1).toUpperCase()}${d.slice(1)} · ${e.local_start_time.slice(0, 5)}`;
}

/** El texto de un evento que no está a la venta (o null si lo está). */
export function textoSinVenta(e: Pick<EventoPublico, 'sale_mode' | 'sold_out' | 'sale_starts_at' | 'ticket_types' | 'timezone' | 'starts_at' | 'local_start_date'>): string | null {
    switch (estadoVenta(e)) {
        case 'solo-listas':
            return 'Esta noche es solo con lista de invitados. Pídele el enlace a tu relaciones públicas.';
        case 'agotado':
            return 'Se han agotado las entradas. Si alguien no termina de pagar a tiempo, alguna puede volver a quedar libre.';
        case 'venta-no-abierta':
            return `Las entradas salen a la venta el ${diaLargo(hoyEn(e.timezone, new Date(e.sale_starts_at ?? e.starts_at)))} a las ${horaEn(e.sale_starts_at ?? e.starts_at, e.timezone)}. Hasta entonces no se pueden comprar.`;
        case 'sin-venta':
            return 'Ahora no hay entradas a la venta para este evento.';
        default:
            return null;
    }
}

/** Lo que se acepta al comprar (10.13 «Antes de comprar»). */
export const CONDICIONES = 'Las entradas no se devuelven si no vas. Si el evento se cancela, te devolvemos todo, gastos de gestión incluidos; si cambia de fecha, podrás cancelarlas gratis.';

/** `?rrpp=<código>` de la venta atribuida a un RRPP (lo guarda Eventos sin interpretarlo). */
export function codigoRrpp(busqueda: URLSearchParams): string | null {
    const v = busqueda.get('rrpp');
    return v !== null && /^[A-Za-z0-9_-]{1,64}$/.test(v) ? v : null;
}

/**
 * Por dónde llega el pase (G787): el canal con el que se identificó el comprador, sin elegirlo. Con el teléfono, por
 * WhatsApp; con el email, por email.
 */
export interface ContactoCompra {
    tipo: 'telefono' | 'email';
    /** Como se enseña («+34 612 34 56 78» o el email). */
    valor: string;
}

/**
 * El contacto con el que se identificó el cliente (G934, ADR-0238): lo dice la API en `MyProfile.identified_with`; el panel
 * ya no elige entre el teléfono y el email de la cuenta.
 */
export function contactoIdentificado(identificado: MiPerfil['identified_with'] | undefined): ContactoCompra | null {
    if (identificado == null) return null;
    return identificado.kind === 'email' ? { tipo: 'email', valor: identificado.value } : { tipo: 'telefono', valor: telefonoLegible(identificado.value) };
}

/** «por WhatsApp al +34 …» / «por email a …». */
export function porDonde(c: ContactoCompra): string {
    return c.tipo === 'email' ? `por email a ${c.valor}` : `por WhatsApp al ${c.valor}`;
}

/** Lo que dice «Elegir» del envío (G787): antes de identificarse se explica; ya identificado, a dónde van. */
export function textoEnvio(c: ContactoCompra | null): { titulo: string; sub: string } {
    return c === null
        ? { titulo: 'Te las mandamos al momento', sub: 'Por WhatsApp si te identificas con tu teléfono, o por email si lo haces con tu email. Cada entrada lleva su pase con un QR para la puerta.' }
        : { titulo: `Te las mandamos ${porDonde(c)}`, sub: 'Con el que te identificaste. Cada entrada lleva su pase con un QR para la puerta.' };
}

/** Lo que dice «Hecha» (resultado con el pase) según el canal. */
export function textoHecha(c: ContactoCompra | null): string {
    if (c === null) {
        return 'Te las hemos mandado por tu canal de contacto. Enseña el QR de cada una en la puerta.';
    }
    return c.tipo === 'email'
        ? `Te las hemos mandado por email a ${c.valor}, con los botones para añadirlas a Apple Wallet o Google Wallet. Enseña el QR de cada una en la puerta.`
        : `Te las hemos mandado por WhatsApp al ${c.valor}. Enseña el QR de cada una en la puerta.`;
}

/** El título y la línea de cada pase en «Hecha»: «Entrada General» (una) o «Entrada 2 de 3»; tipo y nombre o puertas. */
export function lineaDePase(i: number, total: number, tipo: string, titular: string | null | undefined, puertas: string | null): { titulo: string; sub: string } {
    const titulo = total === 1 ? `Entrada ${tipo}` : `Entrada ${String(i + 1)} de ${String(total)}`;
    const sub = titular != null && titular !== '' ? `${tipo} · ${titular}` : puertas === null ? tipo : `${tipo} · desde las ${puertas}`;
    return { titulo, sub };
}

/** A 5 minutos o menos, «Caduca pronto» (como 4.9). */
export const CADUCA_PRONTO_SEGUNDOS = 300;
/** Las entradas se guardan 30 minutos mientras se paga (G39). */
export const GUARDADAS_SEGUNDOS = 30 * 60;

/** El pedido que se está pagando, guardado en la pestaña para leer su estado a la vuelta del pago (`?vuelta=1`). */
export const CLAVE_PEDIDO_ENTRADAS = 'reservaria.entradas.pedido';

export interface PedidoPendiente {
    pedido: string;
    /** `order_token` del alta (`getOnlineTicketOrderStatus`). */
    token: string;
    /** El perfil del cliente en el negocio (`getMyTicketOrder` y `getMyPass`). */
    perfil: string | null;
    evento: string;
    contacto: ContactoCompra | null;
    total: { amount: number; currency: string };
    /** «2 × General», para «Te devolvemos el dinero». */
    resumen: string;
}

type Almacen = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function leerPendiente(almacen: Almacen): PedidoPendiente | null {
    try {
        const v: unknown = JSON.parse(almacen.getItem(CLAVE_PEDIDO_ENTRADAS) ?? 'null');
        return typeof v === 'object' && v !== null && typeof (v as Record<string, unknown>).pedido === 'string' && typeof (v as Record<string, unknown>).token === 'string' && typeof (v as Record<string, unknown>).evento === 'string' ? (v as PedidoPendiente) : null;
    } catch {
        return null;
    }
}

/** La dirección a la que vuelve el pago: esta misma página con `?vuelta=1` (solo https, como en Pedir cita). */
export function vueltaDe(href: string): string | null {
    try {
        const u = new URL(href);
        if (u.protocol !== 'https:') return null;
        u.searchParams.set('vuelta', '1');
        u.hash = '';
        return u.toString();
    } catch {
        return null;
    }
}

/**
 * Qué se enseña a la vuelta del pago según `OnlineOrderStatus.status` (10.13: hecha, esperando al banco, liberadas…). Con
 * `failure_reason` (G937), el cobro falló: «No se ha podido cobrar», aunque el pedido siga pendiente hasta que caduque.
 */
export type ResultadoVuelta = 'hecha' | 'procesando' | 'liberada' | 'rechazado' | 'sin-plaza';

export function resultadoDeEstado(estado: string, motivo: string | null = null): ResultadoVuelta {
    if (motivo !== null && estado === 'pending_payment') {
        return 'rechazado';
    }
    switch (estado) {
        case 'confirmed':
            return 'hecha';
        case 'pending_payment':
            return 'procesando';
        case 'expired':
            return 'liberada';
        case 'refunded':
            return 'sin-plaza';
        default:
            return 'rechazado';
    }
}

/**
 * El texto de «No se ha podido cobrar» según el motivo del fallo (`OnlineOrderStatus.failure_reason`, G937): el código de
 * Pagos se traduce aquí, nunca se enseña tal cual. Sin motivo (o uno que no se conoce), el texto general.
 */
export function textoDelFallo(motivo: string | null | undefined): string {
    const general = 'No te hemos cobrado nada y ya no te guardamos las entradas. Si todavía quieres ir, vuelve a comprarlas.';
    switch (motivo) {
        case 'card_declined':
            return `Tu banco ha rechazado la tarjeta. ${general}`;
        case 'bank_rejected':
            return `Tu banco ha rechazado el pago. ${general}`;
        case 'authentication_required':
            return `Tu banco pedía confirmar el pago y no se completó. ${general}`;
        default:
            return general;
    }
}
